/**
 * Postprocessing Settings Management Module
 * Handles UI controls, localStorage persistence, and parameter collection
 * for arrangement postprocessing settings
 */

import { showToast } from "../modules/toast.js";
import { globalState } from "./globals.js";

let gapRuleCounter = 0;
let fixedToothCounter = 0;

/**
 * Default postprocessing settings
 * These values are used as fallback when server is unavailable
 */
let DEFAULT_POSTPROCESSING_SETTINGS = {
    enabled: true,
    gap_min: "0.22",
    gap_max: "0.30",
    tightness: "0.26",
    midline_mode: "adjust",
    archform: "disabled"
};

/**
 * Load default postprocessing settings from server
 * Falls back to defaults if server request fails
 */
async function loadDefaultSettingsFromServer() {
    try {
        const response = await fetch("/api/postprocessing-defaults");
        if (response.ok) {
            const serverDefaults = await response.json();
            // Convert numeric values to strings for UI compatibility
            DEFAULT_POSTPROCESSING_SETTINGS = {
                enabled: true,
                gap_min: String(serverDefaults.gap_min || 0.22),
                gap_max: String(serverDefaults.gap_max || 0.3),
                tightness: String(serverDefaults.tightness || 0.26),
                midline_mode: serverDefaults.midline_mode || "adjust",
                archform: "disabled"
            };
            populateArchformDropdown(serverDefaults.archform_options);
            // Update minimum threshold for validation (use gap_max from server)
            MIN_GAP_MAX_THRESHOLD = parseFloat(serverDefaults.gap_max || 0.3);
            console.log(
                "✓ Loaded postprocessing defaults from server:",
                DEFAULT_POSTPROCESSING_SETTINGS
            );
            console.log(
                `  Minimum gap_max threshold for validation: ${MIN_GAP_MAX_THRESHOLD}`
            );
        } else {
            console.warn(
                "Failed to load postprocessing defaults from server (status:",
                response.status,
                "), using fallback defaults"
            );
            return false;
        }
    } catch (error) {
        console.warn(
            "Error loading postprocessing defaults from server:",
            error,
            "Using hardcoded defaults"
        );
        return false;
    }
}

/**
 * Fill the archform menu from the server's list.
 *
 * The list comes from the template file rather than being restated here, so a new
 * archform becomes selectable without a frontend change. Called before saved
 * settings are restored, so the saved value has an option to select. Leaves the
 * static Disabled fallback in place if the server sent nothing usable.
 *
 * @param {Array<{value: string, label: string, lower_label: string}>} options
 */
function populateArchformDropdown(options) {
    const dropdown = document.getElementById("archform-dropdown");
    if (!dropdown || !Array.isArray(options) || options.length === 0) {
        return;
    }

    dropdown.innerHTML = "";
    options.forEach((option) => {
        if (!option || !option.value) {
            return;
        }
        const element = document.createElement("option");
        element.value = option.value;
        element.textContent = option.label || option.value;
        if (option.lower_label) {
            element.title = `Lower arch: ${option.lower_label}`;
        }
        dropdown.appendChild(element);
    });
}

/**
 * Update HTML elements with default values from server
 * Called after loading defaults to ensure UI shows correct initial values
 * Only updates if values haven't been set yet (empty or placeholder)
 */
function updateHTMLElementsWithDefaults() {
    const gapMinSlider = document.getElementById("gap-min-slider");
    const gapMaxSlider = document.getElementById("gap-max-slider");
    const tightnessSlider = document.getElementById("tightness-slider");
    const gapMinValue = document.getElementById("gap-min-value");
    const gapMaxValue = document.getElementById("gap-max-value");
    const tightnessValue = document.getElementById("tightness-value");

    // Check if values are empty or placeholder ("--")
    // Range inputs return empty string if value attribute is not set
    const gapMinEmpty =
        !gapMinSlider || !gapMinSlider.value || gapMinSlider.value === "";
    const gapMaxEmpty =
        !gapMaxSlider || !gapMaxSlider.value || gapMaxSlider.value === "";
    const tightnessEmpty =
        !tightnessSlider ||
        !tightnessSlider.value ||
        tightnessSlider.value === "";

    if (gapMinSlider && gapMinEmpty) {
        gapMinSlider.value = DEFAULT_POSTPROCESSING_SETTINGS.gap_min;
        if (gapMinValue) {
            gapMinValue.textContent = parseFloat(
                DEFAULT_POSTPROCESSING_SETTINGS.gap_min
            ).toFixed(2);
        }
    }

    if (gapMaxSlider && gapMaxEmpty) {
        gapMaxSlider.value = DEFAULT_POSTPROCESSING_SETTINGS.gap_max;
        if (gapMaxValue) {
            gapMaxValue.textContent = parseFloat(
                DEFAULT_POSTPROCESSING_SETTINGS.gap_max
            ).toFixed(2);
        }
    }

    if (tightnessSlider && tightnessEmpty) {
        tightnessSlider.value = DEFAULT_POSTPROCESSING_SETTINGS.tightness;
        if (tightnessValue) {
            tightnessValue.textContent = parseFloat(
                DEFAULT_POSTPROCESSING_SETTINGS.tightness
            ).toFixed(2);
        }
    }
}

/**
 * Initialize postprocessing controls and event listeners
 */
export async function initializePostprocessingControls() {
    const enableCheckbox = document.getElementById(
        "enable-postprocessing-checkbox"
    );
    const controlsContainer = document.getElementById(
        "postprocessing-controls"
    );

    if (!enableCheckbox || !controlsContainer) {
        return;
    }

    // Load defaults from server first; saved settings from localStorage are
    // restored later, AFTER the gap-rules and fixed-teeth tables are initialized
    // (loadPostprocessingSettings needs window.addGapRuleFromData /
    // window.addFixedToothFromData, which those init functions assign).
    const defaultsLoaded = await loadDefaultSettingsFromServer();

    // Update HTML elements with default values if they haven't been set yet
    // This ensures HTML shows correct values even before localStorage is checked
    if (defaultsLoaded) {
        updateHTMLElementsWithDefaults();
    }

    // Toggle controls visibility and disabled state
    function togglePostprocessingControls() {
        const isEnabled = enableCheckbox.checked;
        if (isEnabled) {
            controlsContainer.style.display = "flex";
            controlsContainer.classList.remove("disabled");
            enablePostprocessingInputs(true);
        } else {
            controlsContainer.style.display = "none";
            controlsContainer.classList.add("disabled");
            enablePostprocessingInputs(false);
        }
        // Save settings when checkbox changes
        savePostprocessingSettings();
    }

    // Enable/disable all postprocessing inputs
    function enablePostprocessingInputs(enabled) {
        const inputs = controlsContainer.querySelectorAll(
            "input, select, button"
        );
        inputs.forEach((input) => {
            input.disabled = !enabled;
        });
    }

    // Initialize range slider value displays
    function updateRangeValue(sliderId, valueId) {
        const slider = document.getElementById(sliderId);
        const valueDisplay = document.getElementById(valueId);
        if (slider && valueDisplay) {
            slider.addEventListener("input", (e) => {
                valueDisplay.textContent = parseFloat(e.target.value).toFixed(
                    2
                );
                // Save settings when slider changes
                savePostprocessingSettings();
            });
            // Set initial value
            valueDisplay.textContent = parseFloat(slider.value).toFixed(2);
        }
    }

    // Initialize range sliders
    updateRangeValue("gap-min-slider", "gap-min-value");
    updateRangeValue("gap-max-slider", "gap-max-value");
    updateRangeValue("tightness-slider", "tightness-value");

    // Validate gap range (gap_min < gap_max)
    const gapMinSlider = document.getElementById("gap-min-slider");
    const gapMaxSlider = document.getElementById("gap-max-slider");
    const gapMaxValue = document.getElementById("gap-max-value");

    if (gapMinSlider && gapMaxSlider && gapMaxValue) {
        function validateGapRange() {
            const min = parseFloat(gapMinSlider.value);
            const max = parseFloat(gapMaxSlider.value);
            if (min >= max) {
                gapMaxSlider.value = (min + 0.01).toFixed(2);
                gapMaxValue.textContent = gapMaxSlider.value;
            }

            // Warn if gap_max < default threshold (loaded from server)
            if (max < MIN_GAP_MAX_THRESHOLD) {
                showToast(
                    `Warning: max gap (${max.toFixed(
                        2
                    )}mm) is below recommended minimum (${MIN_GAP_MAX_THRESHOLD.toFixed(
                        2
                    )}mm). This may result in unstable postprocessing. Increase tightness if you need to put teeth closer.`,
                    "exclamation-triangle"
                );
            }
        }

        gapMinSlider.addEventListener("input", validateGapRange);
        gapMaxSlider.addEventListener("input", () => {
            const min = parseFloat(gapMinSlider.value);
            let max = parseFloat(gapMaxSlider.value);

            if (max <= min) {
                gapMinSlider.value = (max - 0.01).toFixed(2);
                document.getElementById("gap-min-value").textContent =
                    gapMinSlider.value;
            }

            // Warn if gap_max < default threshold (loaded from server)
            if (max < MIN_GAP_MAX_THRESHOLD) {
                showToast(
                    `Warning: max gap (${max.toFixed(
                        2
                    )}mm) is below recommended minimum (${MIN_GAP_MAX_THRESHOLD.toFixed(
                        2
                    )}mm). This may result in unstable postprocessing. Increase tightness if you need to put teeth closer.`,
                    "exclamation-triangle"
                );
            }

            gapMaxValue.textContent = max.toFixed(2);
        });
    }

    // Initialize custom gap rules table
    initializeCustomGapRules();

    // Initialize freeze (fixed teeth) table
    initializeFixedTeeth();

    // Restore saved settings now that the gap-rules and fixed-teeth tables (and
    // their window.add*FromData restorers) exist - restoring earlier would clear
    // the tables and silently drop saved rules and frozen teeth.
    loadPostprocessingSettings();

    // Set up checkbox toggle
    enableCheckbox.addEventListener("change", togglePostprocessingControls);

    // Add event listener to midline mode dropdown to save settings
    const midlineModeDropdown = document.getElementById(
        "midline-mode-dropdown"
    );
    if (midlineModeDropdown) {
        midlineModeDropdown.addEventListener("change", () => {
            savePostprocessingSettings();
        });
    }

    const archformDropdown = document.getElementById("archform-dropdown");
    if (archformDropdown) {
        archformDropdown.addEventListener("change", () => {
            applyArchformRotationsVisibility();
            savePostprocessingSettings();
        });
    }

    const archformRotationsCheckbox = document.getElementById(
        "enable-archform-rotations-checkbox"
    );
    if (archformRotationsCheckbox) {
        archformRotationsCheckbox.addEventListener("change", () => {
            savePostprocessingSettings();
        });
    }

    // Add reset button functionality
    const resetButton = document.getElementById("reset-postprocessing-button");
    if (resetButton) {
        resetButton.addEventListener("click", () => {
            resetPostprocessingSettings();
        });
    }

    // Initial state
    togglePostprocessingControls();
}

/**
 * Initialize custom gap rules table
 */
function initializeCustomGapRules() {
    const addButton = document.getElementById("add-gap-rule-button");
    const tbody = document.getElementById("custom-gap-rules-tbody");
    const table = document.getElementById("custom-gap-rules-table");
    const emptyMessage = document.getElementById("custom-gap-rules-empty");

    if (!addButton || !tbody || !table || !emptyMessage) {
        return;
    }

    function updateTableVisibility() {
        const hasRules = tbody.children.length > 0;
        table.style.display = hasRules ? "table" : "none";
        emptyMessage.style.display = hasRules ? "none" : "block";
    }

    // Make addGapRule globally accessible for loading saved settings
    window.addGapRuleFromData = function addGapRule(
        toothA = "",
        toothB = "",
        gapMin = "",
        gapMax = ""
    ) {
        const row = document.createElement("tr");
        row.dataset.ruleId = `rule-${gapRuleCounter++}`;

        const toothACell = document.createElement("td");
        const toothAInput = document.createElement("input");
        toothAInput.type = "number";
        toothAInput.value = toothA;
        toothAInput.placeholder = "e.g., 15";
        toothAInput.min = "11";
        toothAInput.max = "48";
        toothACell.appendChild(toothAInput);

        const toothBCell = document.createElement("td");
        const toothBInput = document.createElement("input");
        toothBInput.type = "number";
        toothBInput.value = toothB;
        toothBInput.placeholder = "e.g., 16";
        toothBInput.min = "11";
        toothBInput.max = "48";
        toothBCell.appendChild(toothBInput);

        const gapMinCell = document.createElement("td");
        const gapMinInput = document.createElement("input");
        gapMinInput.type = "number";
        gapMinInput.value = gapMin;
        // Use dynamic default from server (or fallback if not loaded yet)
        gapMinInput.placeholder =
            DEFAULT_POSTPROCESSING_SETTINGS.gap_min || "0.22";
        gapMinInput.step = "0.01";
        gapMinInput.min = "0";
        gapMinCell.appendChild(gapMinInput);

        const gapMaxCell = document.createElement("td");
        const gapMaxInput = document.createElement("input");
        gapMaxInput.type = "number";
        gapMaxInput.value = gapMax;
        // Use dynamic default from server (or fallback if not loaded yet)
        gapMaxInput.placeholder =
            DEFAULT_POSTPROCESSING_SETTINGS.gap_max || "0.30";
        gapMaxInput.step = "0.01";
        // Minimum gap_max should be at least gap_min from defaults
        // This ensures gap_max >= gap_min constraint can be satisfied
        const minGapMax = parseFloat(
            DEFAULT_POSTPROCESSING_SETTINGS.gap_min || 0.22
        );
        gapMaxInput.min = String(Math.max(0, minGapMax - 0.01)); // Allow slightly below gap_min for flexibility
        gapMaxCell.appendChild(gapMaxInput);

        // Validate gap_min < gap_max and warn if gap_max < recommended minimum
        // Use a flag to prevent recursive validation
        let isValidating = false;

        function validateRuleGaps() {
            if (isValidating) return;
            isValidating = true;

            const minStr = gapMinInput.value.trim();
            const maxStr = gapMaxInput.value.trim();
            const min = minStr !== "" ? parseFloat(minStr) : NaN;
            let max = maxStr !== "" ? parseFloat(maxStr) : NaN;

            // Only validate if both values are valid numbers
            if (!isNaN(min) && !isNaN(max)) {
                if (min >= max) {
                    gapMaxInput.value = (min + 0.01).toFixed(2);
                }

                // Warn if gap_max < recommended minimum (from server defaults)
                if (max < MIN_GAP_MAX_THRESHOLD) {
                    showToast(
                        `Warning: max gap (${max.toFixed(
                            2
                        )}mm) is below recommended minimum (${MIN_GAP_MAX_THRESHOLD.toFixed(
                            2
                        )}mm). This may result in unstable postprocessing. Increase tightness if you need to put teeth closer.`,
                        "exclamation-triangle"
                    );
                }
            }

            isValidating = false;
        }

        function validateRuleGapsReverse() {
            if (isValidating) return;
            isValidating = true;

            const minStr = gapMinInput.value.trim();
            const maxStr = gapMaxInput.value.trim();
            const min = minStr !== "" ? parseFloat(minStr) : NaN;
            let max = maxStr !== "" ? parseFloat(maxStr) : NaN;

            // Only validate if both values are valid numbers
            if (!isNaN(min) && !isNaN(max)) {
                if (max <= min) {
                    gapMinInput.value = (max - 0.01).toFixed(2);
                }

                // Warn if gap_max < recommended minimum (from server defaults)
                if (max < MIN_GAP_MAX_THRESHOLD) {
                    showToast(
                        `Warning: max gap (${max.toFixed(
                            2
                        )}mm) is below recommended minimum (${MIN_GAP_MAX_THRESHOLD.toFixed(
                            2
                        )}mm). This may result in unstable postprocessing. Increase tightness if you need to put teeth closer.`,
                        "exclamation-triangle"
                    );
                }
            }

            isValidating = false;
        }

        gapMinInput.addEventListener("blur", validateRuleGaps);
        gapMaxInput.addEventListener("blur", validateRuleGapsReverse);

        const actionsCell = document.createElement("td");
        const removeButton = document.createElement("button");
        removeButton.type = "button";
        removeButton.className = "remove-rule-button";
        removeButton.textContent = "Remove";
        removeButton.addEventListener("click", () => {
            row.remove();
            updateTableVisibility();
            // Save settings when rule is removed
            savePostprocessingSettings();
        });
        actionsCell.appendChild(removeButton);

        // Add event listeners to save settings when inputs change
        [toothAInput, toothBInput, gapMinInput, gapMaxInput].forEach(
            (input) => {
                input.addEventListener("change", () => {
                    savePostprocessingSettings();
                });
            }
        );

        row.appendChild(toothACell);
        row.appendChild(toothBCell);
        row.appendChild(gapMinCell);
        row.appendChild(gapMaxCell);
        row.appendChild(actionsCell);

        tbody.appendChild(row);
        updateTableVisibility();
    };

    addButton.addEventListener("click", () => {
        window.addGapRuleFromData();
        // Save settings when new rule is added
        savePostprocessingSettings();
    });

    updateTableVisibility();
}

// When False, the "Molar Movement Correction" toggle is hidden and the
// correction stays at its config-driven default (on, per the shipped
// postprocessing_config.json). Controlled by the server setting
// ENABLE_MOLAR_MOVEMENT_TOGGLE.
let molarMovementToggleEnabled = true;

/**
 * Show/hide the Archform Rotations checkbox to match the selector it depends on.
 *
 * With no archform selected there is no curve to turn a tooth onto, and the
 * server ignores the flag entirely in that case -- so leaving the checkbox live
 * offers a control that does nothing. Visibility only: the checked state is left
 * alone, so re-selecting an archform restores whatever the technician had chosen
 * rather than silently resetting it to the default.
 */
export function applyArchformRotationsVisibility() {
    const dropdown = document.getElementById("archform-dropdown");
    const group = document.getElementById("archform-rotations-control-group");
    if (!group) {
        return;
    }
    const selection = dropdown ? dropdown.value : "disabled";
    group.style.display = selection && selection !== "disabled" ? "" : "none";
}

/**
 * Show/hide the Molar Movement Correction toggle. When hidden, force the
 * checkbox checked so submissions keep the shipped default;
 * collectPostprocessingParameters also honors the flag so saved state cannot
 * turn it off behind a hidden toggle.
 */
export function applyMolarMovementToggleGate(enabled) {
    molarMovementToggleEnabled = enabled !== false;
    const group = document.getElementById("molar-movement-control-group");
    const checkbox = document.getElementById("enable-molar-movement-checkbox");
    if (group) {
        group.style.display = molarMovementToggleEnabled ? "" : "none";
    }
    if (!molarMovementToggleEnabled && checkbox) {
        checkbox.checked = true;
    }
}

/**
 * Initialize the freeze (fixed teeth) table: each row is one FDI number the
 * user wants held immovable during arrangement. Mirrors the custom
 * gap rules table -- the DOM is the source of truth, re-scraped on collect.
 */
function initializeFixedTeeth() {
    const addButton = document.getElementById("add-fixed-tooth-button");
    const tbody = document.getElementById("fixed-teeth-tbody");
    const table = document.getElementById("fixed-teeth-table");
    const emptyMessage = document.getElementById("fixed-teeth-empty");

    if (!addButton || !tbody || !table || !emptyMessage) {
        return;
    }

    function updateTableVisibility() {
        const hasTeeth = tbody.children.length > 0;
        table.style.display = hasTeeth ? "table" : "none";
        emptyMessage.style.display = hasTeeth ? "none" : "block";
    }

    // Exposed globally so loadPostprocessingSettings() can restore saved teeth.
    window.addFixedToothFromData = function addFixedTooth(fdi = "") {
        const row = document.createElement("tr");
        row.dataset.fixedToothId = `fixed-${fixedToothCounter++}`;

        const toothCell = document.createElement("td");
        const toothInput = document.createElement("input");
        toothInput.type = "number";
        toothInput.value = fdi;
        toothInput.placeholder = "e.g., 11";
        toothInput.min = "11";
        toothInput.max = "48";
        toothInput.addEventListener("change", () => {
            savePostprocessingSettings();
        });
        toothCell.appendChild(toothInput);

        const actionsCell = document.createElement("td");
        const removeButton = document.createElement("button");
        removeButton.type = "button";
        removeButton.className = "remove-rule-button";
        removeButton.textContent = "Remove";
        removeButton.addEventListener("click", () => {
            row.remove();
            updateTableVisibility();
            savePostprocessingSettings();
        });
        actionsCell.appendChild(removeButton);

        row.appendChild(toothCell);
        row.appendChild(actionsCell);
        tbody.appendChild(row);
        updateTableVisibility();
    };

    addButton.addEventListener("click", () => {
        window.addFixedToothFromData();
        savePostprocessingSettings();
    });

    updateTableVisibility();
}

/**
 * Read the frozen FDI numbers from the freeze table (deduped, valid ints only).
 * @returns {number[]}
 */
/**
 * Read frozen (fixed) teeth FDIs from the table as-is: no validation, no toasts.
 * Used for persistence so that what is saved matches exactly what is visible -
 * an invalid FDI stays in the table AND in localStorage instead of being
 * silently dropped from storage. Validation and operator feedback live in
 * collectFixedTeeth(), which runs at submit time.
 */
export function readFixedTeethRaw() {
    const fixedTeeth = [];
    const tbody = document.getElementById("fixed-teeth-tbody");
    if (tbody) {
        tbody.querySelectorAll("tr").forEach((row) => {
            const input = row.querySelector("input");
            if (!input) return;
            const raw = input.value.trim();
            if (raw === "") return;
            const fdi = parseInt(raw, 10);
            if (Number.isNaN(fdi)) return;
            fixedTeeth.push(fdi);
        });
    }
    return fixedTeeth;
}

/**
 * Permanent-tooth FDI test: quadrants 1-4, positions 1-8 (FDI 11-48), mirroring
 * the server. Pure predicate (no toast) so callers that only need the check -
 * e.g. gating whether the freeze table is non-empty - can reuse it.
 * @param {number} fdi
 * @returns {boolean}
 */
export function isPermanentToothFdi(fdi) {
    const quadrant = Math.floor(fdi / 10);
    const position = fdi % 10;
    return quadrant >= 1 && quadrant <= 4 && position >= 1 && position <= 8;
}

export function collectFixedTeeth() {
    const fixedTeeth = [];
    const seen = new Set();
    readFixedTeethRaw().forEach((fdi) => {
        // Permanent teeth only (see isPermanentToothFdi). Warn instead of
        // silently dropping so the operator gets the same feedback as the
        // gap-range inputs. This runs at submit time only, so the toast is not
        // raised on every save.
        if (!isPermanentToothFdi(fdi)) {
            showToast(
                `Ignoring frozen tooth ${fdi}: not a permanent tooth FDI (11-48).`,
                "exclamation-triangle"
            );
            return;
        }
        if (!seen.has(fdi)) {
            seen.add(fdi);
            fixedTeeth.push(fdi);
        }
    });
    return fixedTeeth;
}

/**
 * Collect postprocessing parameters from UI
 * @returns {Object} Object with enabled flag and parameters
 */
export function collectPostprocessingParameters() {
    const enableCheckbox = document.getElementById(
        "enable-postprocessing-checkbox"
    );
    if (!enableCheckbox || !enableCheckbox.checked) {
        return { enabled: false };
    }

    const gapMin = parseFloat(document.getElementById("gap-min-slider").value);
    const gapMax = parseFloat(document.getElementById("gap-max-slider").value);
    const tightness = parseFloat(
        document.getElementById("tightness-slider").value
    );
    const midlineMode = document.getElementById("midline-mode-dropdown").value;
    const archformDropdown = document.getElementById("archform-dropdown");
    const archform = archformDropdown ? archformDropdown.value : "disabled";
    const molarMovementCheckbox = document.getElementById(
        "enable-molar-movement-checkbox"
    );
    const enableMolarMovementCorrection =
        !molarMovementToggleEnabled || molarMovementCheckbox?.checked !== false;
    const archformRotationsCheckbox = document.getElementById(
        "enable-archform-rotations-checkbox"
    );
    // The checkbox ships ticked, so a missing element means "the default", not
    // "off": send true, the same thing the server would have chosen anyway.
    const enableArchformRotations = archformRotationsCheckbox
        ? !!archformRotationsCheckbox.checked
        : true;

    // Collect custom gap rules
    const customGapRules = [];
    const tbody = document.getElementById("custom-gap-rules-tbody");
    if (tbody) {
        const rows = tbody.querySelectorAll("tr");
        rows.forEach((row) => {
            const inputs = row.querySelectorAll("input");
            if (inputs.length >= 4) {
                const toothA = inputs[0].value.trim();
                const toothB = inputs[1].value.trim();
                const gapMin = inputs[2].value.trim();
                const gapMax = inputs[3].value.trim();

                // Only add if tooth_a and tooth_b are provided
                if (toothA && toothB) {
                    const rule = {
                        tooth_a: parseInt(toothA),
                        tooth_b: parseInt(toothB)
                    };

                    if (gapMin) {
                        rule.gap_min = parseFloat(gapMin);
                    }
                    if (gapMax) {
                        rule.gap_max = parseFloat(gapMax);
                    }

                    customGapRules.push(rule);
                }
            }
        });
    }

    const postprocessingParams = {
        gap_min: gapMin,
        gap_max: gapMax,
        tightness: tightness,
        midline_mode: midlineMode,
        enable_molar_movement_correction: enableMolarMovementCorrection,
        archform: archform,
        enable_archform_rotations: enableArchformRotations
    };

    if (customGapRules.length > 0) {
        postprocessingParams.custom_gap_rules = customGapRules;
    }

    const fixedTeeth = collectFixedTeeth();
    if (fixedTeeth.length > 0) {
        postprocessingParams.fixed_teeth = fixedTeeth;
    }

    return {
        enabled: true,
        parameters: postprocessingParams
    };
}

/**
 * Save postprocessing settings to localStorage
 */
export function savePostprocessingSettings() {
    try {
        const enableCheckbox = document.getElementById(
            "enable-postprocessing-checkbox"
        );
        const gapMinSlider = document.getElementById("gap-min-slider");
        const gapMaxSlider = document.getElementById("gap-max-slider");
        const tightnessSlider = document.getElementById("tightness-slider");
        const midlineModeDropdown = document.getElementById(
            "midline-mode-dropdown"
        );

        if (
            !enableCheckbox ||
            !gapMinSlider ||
            !gapMaxSlider ||
            !tightnessSlider ||
            !midlineModeDropdown
        ) {
            return;
        }

        // Collect custom gap rules
        const customGapRules = [];
        const tbody = document.getElementById("custom-gap-rules-tbody");
        if (tbody) {
            const rows = tbody.querySelectorAll("tr");
            rows.forEach((row) => {
                const inputs = row.querySelectorAll("input");
                if (inputs.length >= 4) {
                    const toothA = inputs[0].value.trim();
                    const toothB = inputs[1].value.trim();
                    const gapMin = inputs[2].value.trim();
                    const gapMax = inputs[3].value.trim();

                    if (toothA && toothB) {
                        customGapRules.push({
                            tooth_a: toothA,
                            tooth_b: toothB,
                            gap_min: gapMin,
                            gap_max: gapMax
                        });
                    }
                }
            });
        }

        const molarMovementCheckbox = document.getElementById(
            "enable-molar-movement-checkbox"
        );
        const archformDropdown = document.getElementById("archform-dropdown");

        const settings = {
            enabled: enableCheckbox.checked,
            gap_min: gapMinSlider.value,
            gap_max: gapMaxSlider.value,
            tightness: tightnessSlider.value,
            midline_mode: midlineModeDropdown.value,
            custom_gap_rules: customGapRules,
            // Persist the raw table contents (no validation/toast) so a reload
            // restores exactly what is on screen; validation happens at submit.
            fixed_teeth: readFixedTeethRaw(),
            enable_molar_movement_correction: molarMovementCheckbox
                ? molarMovementCheckbox.checked
                : true,
            archform: archformDropdown ? archformDropdown.value : "disabled",
            enable_archform_rotations:
                document.getElementById("enable-archform-rotations-checkbox")
                    ?.checked !== false
        };

        localStorage.setItem(
            "postprocessingSettings",
            JSON.stringify(settings)
        );
        console.log("Postprocessing settings saved to localStorage");
    } catch (error) {
        console.error("Error saving postprocessing settings:", error);
    }
}

/**
 * Load postprocessing settings from localStorage
 */
export function loadPostprocessingSettings() {
    try {
        const savedSettings = localStorage.getItem("postprocessingSettings");

        // Get UI elements
        const enableCheckbox = document.getElementById(
            "enable-postprocessing-checkbox"
        );
        const gapMinSlider = document.getElementById("gap-min-slider");
        const gapMaxSlider = document.getElementById("gap-max-slider");
        const tightnessSlider = document.getElementById("tightness-slider");
        const midlineModeDropdown = document.getElementById(
            "midline-mode-dropdown"
        );

        if (!savedSettings) {
            console.log(
                "No saved postprocessing settings found - using defaults"
            );
            // Apply default settings for new users
            if (enableCheckbox) {
                enableCheckbox.checked =
                    DEFAULT_POSTPROCESSING_SETTINGS.enabled;
            }
            if (gapMinSlider) {
                gapMinSlider.value = DEFAULT_POSTPROCESSING_SETTINGS.gap_min;
                const gapMinValue = document.getElementById("gap-min-value");
                if (gapMinValue) {
                    gapMinValue.textContent = parseFloat(
                        DEFAULT_POSTPROCESSING_SETTINGS.gap_min
                    ).toFixed(2);
                }
            }
            if (gapMaxSlider) {
                gapMaxSlider.value = DEFAULT_POSTPROCESSING_SETTINGS.gap_max;
                const gapMaxValue = document.getElementById("gap-max-value");
                if (gapMaxValue) {
                    gapMaxValue.textContent = parseFloat(
                        DEFAULT_POSTPROCESSING_SETTINGS.gap_max
                    ).toFixed(2);
                }
            }
            if (tightnessSlider) {
                tightnessSlider.value =
                    DEFAULT_POSTPROCESSING_SETTINGS.tightness;
                const tightnessValue =
                    document.getElementById("tightness-value");
                if (tightnessValue) {
                    tightnessValue.textContent = parseFloat(
                        DEFAULT_POSTPROCESSING_SETTINGS.tightness
                    ).toFixed(2);
                }
            }
            if (midlineModeDropdown) {
                midlineModeDropdown.value =
                    DEFAULT_POSTPROCESSING_SETTINGS.midline_mode;
            }
            const archformDropdown =
                document.getElementById("archform-dropdown");
            if (archformDropdown) {
                archformDropdown.value =
                    DEFAULT_POSTPROCESSING_SETTINGS.archform;
            }
            const archformRotationsCheckbox = document.getElementById(
                "enable-archform-rotations-checkbox"
            );
            if (archformRotationsCheckbox) {
                archformRotationsCheckbox.checked = true;
            }
            applyArchformRotationsVisibility();
            return;
        }

        const settings = JSON.parse(savedSettings);
        console.log(
            "Loading postprocessing settings from localStorage:",
            settings
        );

        // Apply settings to UI
        if (enableCheckbox) {
            enableCheckbox.checked =
                settings.enabled !== undefined ? settings.enabled : true;
        }

        if (gapMinSlider) {
            const gapMin =
                settings.gap_min !== undefined
                    ? settings.gap_min
                    : DEFAULT_POSTPROCESSING_SETTINGS.gap_min;
            gapMinSlider.value = gapMin;
            const gapMinValue = document.getElementById("gap-min-value");
            if (gapMinValue) {
                gapMinValue.textContent = parseFloat(gapMin).toFixed(2);
            }
        }

        if (gapMaxSlider) {
            const gapMax =
                settings.gap_max !== undefined
                    ? settings.gap_max
                    : DEFAULT_POSTPROCESSING_SETTINGS.gap_max;
            gapMaxSlider.value = gapMax;
            const gapMaxValue = document.getElementById("gap-max-value");
            if (gapMaxValue) {
                gapMaxValue.textContent = parseFloat(gapMax).toFixed(2);
            }
        }

        if (tightnessSlider) {
            const tightness =
                settings.tightness !== undefined
                    ? settings.tightness
                    : DEFAULT_POSTPROCESSING_SETTINGS.tightness;
            tightnessSlider.value = tightness;
            const tightnessValue = document.getElementById("tightness-value");
            if (tightnessValue) {
                tightnessValue.textContent = parseFloat(tightness).toFixed(2);
            }
        }

        if (midlineModeDropdown && settings.midline_mode) {
            midlineModeDropdown.value = settings.midline_mode;
        } else if (midlineModeDropdown) {
            // Default to "adjust" if not in saved settings
            midlineModeDropdown.value = "adjust";
        }

        // A saved archform whose template is no longer offered would silently
        // select nothing, so fall back to the default unless the option is present.
        const archformDropdown = document.getElementById("archform-dropdown");
        if (archformDropdown) {
            const saved = settings.archform;
            const offered =
                saved &&
                Array.from(archformDropdown.options).some(
                    (option) => option.value === saved
                );
            archformDropdown.value = offered ? saved : "disabled";
        }

        // Absent in a saved blob written before this option existed, and the
        // default is on, so `!== false` rather than `=== true`.
        const archformRotationsCheckbox = document.getElementById(
            "enable-archform-rotations-checkbox"
        );
        if (archformRotationsCheckbox) {
            archformRotationsCheckbox.checked =
                settings.enable_archform_rotations !== false;
        }
        // Kept next to the dropdown restore above, so the checkbox
        // matches the selection the technician is looking at.
        applyArchformRotationsVisibility();

        const molarMovementCheckbox = document.getElementById(
            "enable-molar-movement-checkbox"
        );
        if (molarMovementCheckbox) {
            // When the toggle is hidden, ignore saved state and stay checked
            // (config-driven default) so a stale "off" value can't turn the
            // correction off behind a hidden toggle.
            molarMovementCheckbox.checked =
                !molarMovementToggleEnabled ||
                settings.enable_molar_movement_correction !== false;
        }

        // Load custom gap rules
        if (
            settings.custom_gap_rules &&
            Array.isArray(settings.custom_gap_rules)
        ) {
            const tbody = document.getElementById("custom-gap-rules-tbody");
            if (tbody) {
                // Clear existing rules first
                tbody.innerHTML = "";

                // Add saved rules
                settings.custom_gap_rules.forEach((rule) => {
                    // Use the addGapRule function if it's accessible, otherwise add manually
                    if (typeof window.addGapRuleFromData === "function") {
                        window.addGapRuleFromData(
                            rule.tooth_a,
                            rule.tooth_b,
                            rule.gap_min,
                            rule.gap_max
                        );
                    }
                });
            }
        }

        // Load frozen (fixed) teeth
        if (settings.fixed_teeth && Array.isArray(settings.fixed_teeth)) {
            const tbody = document.getElementById("fixed-teeth-tbody");
            if (tbody) {
                tbody.innerHTML = "";
                settings.fixed_teeth.forEach((fdi) => {
                    if (typeof window.addFixedToothFromData === "function") {
                        window.addFixedToothFromData(fdi);
                    }
                });
            }
        }

        console.log("Postprocessing settings loaded successfully");
    } catch (error) {
        console.error("Error loading postprocessing settings:", error);
    }
}

/**
 * Update postprocessing section visibility based on current state
 * Section should be visible whenever arrangement mode is active and the
 * user is not in feedback/review mode. This allows running Refine after the
 * initial Process step.
 */
export function updatePostprocessingSectionVisibility() {
    const postprocessingSection = document.getElementById(
        "postprocessing-section"
    );
    if (!postprocessingSection) {
        console.warn("Postprocessing section element not found");
        return;
    }

    const processingModeDropdown = document.getElementById(
        "processing-mode-dropdown"
    );
    const processingMode = processingModeDropdown?.value || "arrangement";
    const isFeedbackMode = globalState.feedbackMode || false;
    const shouldShow = processingMode === "arrangement" && !isFeedbackMode;

    console.log("updatePostprocessingSectionVisibility:", {
        processingMode,
        isFeedbackMode,
        shouldShow,
        currentDisplay: postprocessingSection.style.display
    });

    // Explicitly set display style to ensure visibility
    if (shouldShow) {
        postprocessingSection.style.display = "block";
        postprocessingSection.style.setProperty(
            "display",
            "block",
            "important"
        );
        console.log("Postprocessing section set to visible");
    } else {
        postprocessingSection.style.display = "none";
        console.log("Postprocessing section set to hidden");
    }

    // Also update gingiva estimation section visibility
    const gingivaEstimationSection = document.getElementById(
        "gingiva-estimation-section"
    );
    if (gingivaEstimationSection) {
        if (shouldShow) {
            gingivaEstimationSection.style.display = "block";
        } else {
            gingivaEstimationSection.style.display = "none";
        }
    }
}

/**
 * Reset postprocessing settings to default values
 */
export function resetPostprocessingSettings() {
    try {
        // Clear localStorage
        localStorage.removeItem("postprocessingSettings");
        console.log("Postprocessing settings cleared from localStorage");

        // Reset UI to default values
        const enableCheckbox = document.getElementById(
            "enable-postprocessing-checkbox"
        );
        const gapMinSlider = document.getElementById("gap-min-slider");
        const gapMaxSlider = document.getElementById("gap-max-slider");
        const tightnessSlider = document.getElementById("tightness-slider");
        const midlineModeDropdown = document.getElementById(
            "midline-mode-dropdown"
        );

        // Reset UI to default values using constants
        if (enableCheckbox) {
            enableCheckbox.checked = DEFAULT_POSTPROCESSING_SETTINGS.enabled;
        }

        if (gapMinSlider) {
            gapMinSlider.value = DEFAULT_POSTPROCESSING_SETTINGS.gap_min;
            const gapMinValue = document.getElementById("gap-min-value");
            if (gapMinValue) {
                gapMinValue.textContent = parseFloat(
                    DEFAULT_POSTPROCESSING_SETTINGS.gap_min
                ).toFixed(2);
            }
        }

        if (gapMaxSlider) {
            gapMaxSlider.value = DEFAULT_POSTPROCESSING_SETTINGS.gap_max;
            const gapMaxValue = document.getElementById("gap-max-value");
            if (gapMaxValue) {
                gapMaxValue.textContent = parseFloat(
                    DEFAULT_POSTPROCESSING_SETTINGS.gap_max
                ).toFixed(2);
            }
        }

        if (tightnessSlider) {
            tightnessSlider.value = DEFAULT_POSTPROCESSING_SETTINGS.tightness;
            const tightnessValue = document.getElementById("tightness-value");
            if (tightnessValue) {
                tightnessValue.textContent = parseFloat(
                    DEFAULT_POSTPROCESSING_SETTINGS.tightness
                ).toFixed(2);
            }
        }

        if (midlineModeDropdown) {
            midlineModeDropdown.value =
                DEFAULT_POSTPROCESSING_SETTINGS.midline_mode;
        }

        const archformDropdown = document.getElementById("archform-dropdown");
        if (archformDropdown) {
            archformDropdown.value = DEFAULT_POSTPROCESSING_SETTINGS.archform;
        }

        // Reset restores defaults and has no saved settings to consult, so the
        // default is written directly. The default is on.
        const archformRotationsCheckbox = document.getElementById(
            "enable-archform-rotations-checkbox"
        );
        if (archformRotationsCheckbox) {
            archformRotationsCheckbox.checked = true;
        }
        // Kept next to the dropdown reset above, so the checkbox
        // matches the selection the technician is looking at.
        applyArchformRotationsVisibility();

        const molarMovementCheckbox = document.getElementById(
            "enable-molar-movement-checkbox"
        );
        if (molarMovementCheckbox) {
            molarMovementCheckbox.checked = true;
        }

        // Clear custom gap rules
        const tbody = document.getElementById("custom-gap-rules-tbody");
        if (tbody) {
            tbody.innerHTML = "";
        }

        // Update table visibility
        const table = document.getElementById("custom-gap-rules-table");
        const emptyMessage = document.getElementById("custom-gap-rules-empty");
        if (table) {
            table.style.display = "none";
        }
        if (emptyMessage) {
            emptyMessage.style.display = "block";
        }

        // Clear frozen (fixed) teeth
        const fixedTbody = document.getElementById("fixed-teeth-tbody");
        if (fixedTbody) {
            fixedTbody.innerHTML = "";
        }
        const fixedTable = document.getElementById("fixed-teeth-table");
        const fixedEmpty = document.getElementById("fixed-teeth-empty");
        if (fixedTable) {
            fixedTable.style.display = "none";
        }
        if (fixedEmpty) {
            fixedEmpty.style.display = "block";
        }

        // Trigger the toggle to update UI state - show controls since enabled by default
        const controlsContainer = document.getElementById(
            "postprocessing-controls"
        );
        if (controlsContainer) {
            controlsContainer.style.display = "flex";
            controlsContainer.classList.remove("disabled");
        }

        // Enable all inputs
        if (controlsContainer) {
            const inputs = controlsContainer.querySelectorAll(
                "input, select, button"
            );
            inputs.forEach((input) => {
                input.disabled = false;
            });
        }

        showToast("Postprocessing settings reset to defaults", "check-circle");
    } catch (error) {
        console.error("Error resetting postprocessing settings:", error);
        showToast("Error resetting settings", "exclamation-triangle");
    }
}
